// ============================================
// SHARED API CLIENT UTILITIES
// ============================================

export const API_BASE = '/api';

/**
 * Generic fetch wrapper with error handling
 * Consolidates the shared logic from api.ts and results.ts
 */
export async function apiFetch<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    const token = localStorage.getItem('admin_token');
    const defaultHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    });

    if (!response.ok) {
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('app:unauthorized'));
        }
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
        return {} as T;
    }

    try {
        const data = JSON.parse(text);
        const isGetEndpoint = endpoint.includes('/get');
        const isListLike = endpoint.includes('/list') || endpoint.includes('/respondents/') || endpoint.includes('/results/');

        if (Array.isArray(data) && isGetEndpoint && !isListLike) {
            return data[0] as T;
        }
        return data as T;
    } catch {
        return text as T;
    }
}
/**
 * Utility to handle binary file downloads from API endpoints
 */
export async function apiDownload(
    endpoint: string,
    options: RequestInit = {}
): Promise<void> {
    const url = `${API_BASE}${endpoint}`;

    const token = localStorage.getItem('admin_token');
    const defaultHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    });

    if (!response.ok) {
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('app:unauthorized'));
        }
        const errorText = await response.text();
        throw new Error(`Download Error: ${response.status} - ${errorText}`);
    }

    // Try to get filename from Content-Disposition header
    let filename = 'reports.zip';
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
        }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
}
